import { Box, makeStyles, Theme, Typography } from "@material-ui/core"
import WarningIcon from "@material-ui/icons/Warning"
import React, { useContext, useMemo, useState } from "react"

import { GetLabelDto, MovementCounterPartyDto, NewFullTransactionDto } from "services/api/openapi"
import CountrolCounterParty from "./ControlCounterParty"
import MovementLabelDrawerModal from "./MovementLabelDrawerModal"
import TitleWithDivider from "components/TitleWithDivider"
import { DrawerProp } from "components/misc/useDrawer"
import PermissionDisabled from "components/Permission/PermissionDisabled"
import AddLabelButton from "./AddLabelButton"
import RegularLabelSelector from "./RegularLabelSelector"
import NonTaxabelLabelSelector, { mockInternalTransferLabel } from "./NonTaxabelLabelSelector"
import ShowLabelAlreadyActivated from "./ShowLabelAlreadyActivated"
import api from "services/api"
import { convertMovements } from "../utils/convertMovement"
import { getMainMovement } from "../GeneratedTable/GeneratedRows/GenerateSimpleTransaction/generateSimpleTransaction"
import { WorkspaceContext } from "../../../services/context/workspaceContext"
import { isLockedTransaction } from "../../../services/utils/isLocked"
import ConditionalTooltip from "../../ConditionalTooltip"
import { DrawerSection } from "../../Drawer/DrawerItems"

const useStyles = makeStyles((theme: Theme) => ({
  containerSection: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  typographyWarning: {
    fontWeight: 800,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  iconWarning: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(0.4),
  },
}))

const TransactionLabelDrawer = (props: DrawerProp<NewFullTransactionDto, false>) => {
  const { item: transaction, onClose } = props
  const classes = useStyles()
  const [labelsToAdd, setLabelsToAdd] = useState<GetLabelDto[]>([])
  const [newLabelName, setNewLabelName] = useState<string | null>(null)
  const [nonTaxablelabelsToAdd, setNonTaxableLabelsToAdd] = useState<GetLabelDto[]>([])
  const [confirmedInternalTransfer, setConfirmedInternalTransfer] = useState<boolean>(false)
  const { workspace } = useContext(WorkspaceContext)

  const { mainMvt } = useMemo(() => {
    const [loneMovements, trades] = convertMovements(transaction)
    const nbIn = transaction.incomingCount
    const nbOut = transaction.outgoingCount
    const nbFee = transaction.feeCount
    const mainMvt = getMainMovement(loneMovements, trades, nbIn, nbOut, nbFee)

    return {
      mainMvt,
    }
  }, [transaction])

  const [nonTaxableLabels] = useState<GetLabelDto[]>([])
  const [selectedGuessedOtherParty, setSelectedGuessedOtherParty] = useState<MovementCounterPartyDto | null>(null)

  const isInternalTransfer = useMemo(() => {
    const isFound = nonTaxablelabelsToAdd.find((label) => label.name === mockInternalTransferLabel.name)

    return isFound !== undefined ? true : false
  }, [nonTaxablelabelsToAdd])

  const isNonTaxableEvent = useMemo(() => {
    const isFound = nonTaxablelabelsToAdd.find((label) => label.name === "Non taxable event")

    return isFound !== undefined ? true : false
  }, [nonTaxablelabelsToAdd])

  const showAddLabelButton = (): boolean =>
    labelsToAdd.length > 0 || nonTaxablelabelsToAdd.length > 0 || isInternalTransfer

  const counterParties = api.transaction.useGuessCounterParties(mainMvt?.id ?? "", {
    enabled: isInternalTransfer,
  })

  const closeDrawer = () => {
    setNewLabelName(null)
    onClose()
  }

  const isLocked = isLockedTransaction(
    workspace.lock.isEnabled,
    transaction.transactionDate,
    workspace.lock.exclusiveEndDate,
  )

  return (
    <>
      <Box mt={3} />
      <TitleWithDivider title="Label list" variant="h5" />
      <ShowLabelAlreadyActivated transaction={transaction} mainMvt={mainMvt} />
      <Box mt={4} mb={2}>
        <PermissionDisabled permission="can_modify_transaction" action="modify a transaction">
          <>
            <ConditionalTooltip tooltipMessage={"Labels can't be applied on locked transaction"} disabled={isLocked}>
              <DrawerSection name="Taxable event">
                <RegularLabelSelector
                  transaction={transaction}
                  setNewLabelName={setNewLabelName}
                  labelsToAdd={labelsToAdd}
                  setLabelsToAdd={setLabelsToAdd}
                  isLockedTransaction={isLocked}
                />
              </DrawerSection>
            </ConditionalTooltip>
            <ConditionalTooltip tooltipMessage={"Labels can't be applied on locked transaction"} disabled={isLocked}>
              <DrawerSection name="Non taxable event">
                <NonTaxabelLabelSelector
                  isInternalTransfer={isInternalTransfer}
                  isNonTaxableEvent={isNonTaxableEvent}
                  transaction={transaction}
                  setNewLabelName={setNewLabelName}
                  nonTaxablelabelsToAdd={nonTaxablelabelsToAdd}
                  setNonTaxableLabelsToAdd={setNonTaxableLabelsToAdd}
                  mainMvt={mainMvt}
                  isLockedTransaction={isLocked}
                />
              </DrawerSection>
            </ConditionalTooltip>
            <Box mt={2} />
            {isInternalTransfer && !isLocked && (
              <CountrolCounterParty
                counterParties={counterParties}
                labelsToAdd={labelsToAdd}
                setConfirmedInternalTransfer={setConfirmedInternalTransfer}
                isInternalTransfer={isInternalTransfer}
                confirmedInternalTransfer={confirmedInternalTransfer}
                setSelectedGuessedOtherParty={setSelectedGuessedOtherParty}
                selectedGuessedOtherParty={selectedGuessedOtherParty}
              />
            )}
            {isNonTaxableEvent && !isLocked && (
              <div className={classes.containerSection}>
                <Typography className={classes.typographyWarning}>
                  <WarningIcon className={classes.iconWarning} /> {"No cost basis is computed on non taxable events"}
                </Typography>
              </div>
            )}
          </>
        </PermissionDisabled>
        {showAddLabelButton() && (
          <AddLabelButton
            isInternalTransfer={isInternalTransfer}
            nonTaxablelabelsToAdd={nonTaxablelabelsToAdd}
            nonTaxableLabels={nonTaxableLabels}
            selectedGuessedOtherParty={selectedGuessedOtherParty}
            confirmedInternalTransfer={confirmedInternalTransfer}
            transaction={transaction}
            onClose={closeDrawer}
            counterParties={counterParties}
            labelsToAdd={labelsToAdd}
            setLabelsToAdd={setLabelsToAdd}
            mainMvt={mainMvt}
          />
        )}
      </Box>
      <MovementLabelDrawerModal labelName={newLabelName} onClose={() => setNewLabelName(null)} movement={mainMvt} />
    </>
  )
}

export default TransactionLabelDrawer
